'use client';

import { useEffect, useState } from 'react';
import { Website } from '~/fecom-api/schema';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { GlobeIcon } from 'lucide-react';
import { Button } from './ui/button';
import Image from 'next/image';
import { useOneTrustContext } from '~/context/onetrust-context';
import { getLogoFromThemeMap } from './layout/header/logo';

type InternationalRedirectDialogProps = {
  countryCode?: string | null;
  website: Website;
};

export const InternationalRedirectDialog = ({
  countryCode,
  website,
}: InternationalRedirectDialogProps) => {
  const [
    shouldShowInternationalRedirectDialog,
    setshouldShowInternationalRedirectDialog,
  ] = useState<boolean>(false);

  const { consentLevels } = useOneTrustContext();

  useEffect(() => {
    // If consentLevels is null, or if only C0001 (Strictly Necessary) is given and the alert box hasn't been closed, do not show the dialog
    if (
      consentLevels === null ||
      (consentLevels.every((c) => c === 'C0001') &&
        document.cookie.indexOf('OptanonAlertBoxClosed=') === -1)
    ) {
      return;
    }
    if (countryCode != null) {
      if (website === Website.InternationalMarketplace) {
        setshouldShowInternationalRedirectDialog(false);
      }
      if (website === Website.FindelInternational && countryCode === 'AE') {
        setshouldShowInternationalRedirectDialog(true);
      }
      if (website !== Website.FindelInternational && countryCode !== 'GB') {
        setshouldShowInternationalRedirectDialog(true);
      }
    }
  }, [consentLevels, countryCode, website]);

  const [open, setOpen] = useState(true);

  let ignoreInternationalRedirect = '0';

  if (typeof window !== 'undefined') {
    ignoreInternationalRedirect =
      window.localStorage.getItem('ignoreInternationalRedirect') ?? '0';
  }

  const handleStay = async () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('ignoreInternationalRedirect', '1');
    }
    setOpen(false);
  };

  const brandLogo = (site: Website) => {
    const logo = getLogoFromThemeMap(site);

    return (
      <div className="relative flex grow items-center justify-center px-4">
        <Image
          src={logo.altLogo ?? logo.logo}
          alt={logo.alt}
          width={logo.width}
          height={logo.height}
          className="h-16 w-full object-contain"
        />
      </div>
    );
  };

  return (
    <>
      {shouldShowInternationalRedirectDialog &&
        ignoreInternationalRedirect != '1' && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
              disableManualClose
              className="max-h-[100svh] overflow-auto border-2 p-0"
            >
              <DialogHeader className="bg-[var(--site-header-bg-color)] px-6 pb-4 pt-4 text-[var(--site-header-text-color)]">
                <div className="flex items-center justify-center gap-3">
                  <GlobeIcon
                    className="h-8 w-8 text-[var(--site-header-text-color)]"
                    aria-label="Globe"
                  />
                  <DialogTitle className="text-2xl font-bold">
                    Shopping from{' '}
                    {countryCode === 'AE' ? 'the UAE' : 'Overseas'}?
                  </DialogTitle>
                </div>
              </DialogHeader>
              <div className="overflow-y-auto">
                <div className="px-6">
                  <p className="mt-2 text-lg">
                    We have detected that your IP address is{' '}
                    {countryCode === 'AE' ? 'UAE based' : 'not UK based'}.
                  </p>
                  <p className="mt-2">
                    We have a specific international website which deals with
                    all {countryCode === 'AE' ? 'AED' : 'non-UK'} transactions
                    and deliveries. Please select which website you would like
                    to continue with.
                  </p>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 px-6 pb-6 md:grid-cols-2 md:gap-8">
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                    <h3 className="mb-2 font-semibold">
                      {countryCode === 'AE'
                        ? 'For UAE Delivery'
                        : 'For International Delivery'}
                    </h3>
                    {brandLogo(
                      countryCode === 'AE'
                        ? Website.InternationalMarketplace
                        : Website.FindelInternational,
                    )}
                    <Button
                      className="mt-auto flex w-full items-center justify-center"
                      onClick={() => {
                        window.location.href =
                          countryCode === 'AE'
                            ? 'https://www.findel-dryad.ae/'
                            : 'https://www.findel-international.com';
                      }}
                    >
                      Switch to{' '}
                      {countryCode === 'AE'
                        ? 'Findel Dryad'
                        : 'Findel International'}
                    </Button>
                  </div>
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                    <h3 className="font-semibold">
                      {website === Website.FindelInternational
                        ? 'For International Delivery'
                        : 'For Delivery in the UK'}
                    </h3>
                    {brandLogo(website)}
                    <Button
                      variant="outline"
                      onClick={handleStay}
                      className="mt-auto flex w-full items-center justify-center"
                    >
                      Continue on the current site
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
    </>
  );
};
